import { useState, useEffect } from 'react'
import { X, Save, Loader2 } from 'lucide-react'
import { memoryApi, MemoryScope, MemoryScopeCreate, MemoryScopeUpdate } from '../../lib/api'

interface ScopeModalProps {
  scope: MemoryScope | null
  onClose: () => void
  onSave: () => void
}

export default function ScopeModal({ scope, onClose, onSave }: ScopeModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    scope_type: 'tenant' as 'tenant' | 'end_user',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (scope) {
      setFormData({
        name: scope.name,
        slug: scope.slug,
        description: scope.description || '',
        scope_type: scope.scope_type,
      })
    }
  }, [scope])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (scope) {
        // Update
        const updateData: MemoryScopeUpdate = {
          name: formData.name,
          description: formData.description || undefined,
        }
        await memoryApi.updateScope(scope.id, updateData)
      } else {
        // Create
        const createData: MemoryScopeCreate = {
          name: formData.name,
          slug: formData.slug || undefined,
          description: formData.description || undefined,
          scope_type: formData.scope_type,
        }
        await memoryApi.createScope(createData)
      }
      onSave()
      onClose()
    } catch (err: any) {
      console.error('Failed to save scope:', err)
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde')
    } finally {
      setLoading(false)
    }
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: scope ? prev.slug : generateSlug(name),
    }))
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
            {scope ? 'Modifier le scope' : 'Nouveau scope'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Nom du scope <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Ex: Support Client, Connaissances Produit"
              required
              className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-zinc-500">
              Nom descriptif pour identifier ce scope
            </p>
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Slug {scope && <span className="text-xs text-zinc-500">(non modifiable)</span>}
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              placeholder="support-client"
              disabled={!!scope}
              className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-zinc-500">
              Identifiant unique (généré automatiquement)
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Décrivez l'objectif de ce scope..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Scope Type (only for creation) */}
          {!scope && (
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                Type de scope
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, scope_type: 'tenant' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.scope_type === 'tenant'
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-zinc-300 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-600'
                  }`}
                >
                  <div className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                    Business (Tenant)
                  </div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-400">
                    Connaissances partagées pour tous vos agents
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, scope_type: 'end_user' }))}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.scope_type === 'end_user'
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-zinc-300 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-600'
                  }`}
                >
                  <div className="font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                    End-User
                  </div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-400">
                    Mémoire personnalisée par client
                  </div>
                </button>
              </div>
              <p className="mt-2 text-xs text-zinc-500">
                {formData.scope_type === 'tenant' 
                  ? 'Les faits seront partagés entre tous les agents liés à ce scope'
                  : 'Les faits seront spécifiques à chaque client identifié (Phase C)'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 rounded-full border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name}
              className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {scope ? 'Mettre à jour' : 'Créer le scope'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
